import { useEffect, useState, useCallback, useRef } from "react";
import api, { euro, downloadFile } from "@/lib/api";
import BefTable from "@/components/BefTable";
import CommessaKpiTable from "@/components/CommessaKpiTable";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Maximize2, X as XIcon, FileSpreadsheet as FileXls } from "lucide-react";
import PageHeader from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import DatePickerField from "@/components/DatePickerField";
import MultiSelect from "@/components/MultiSelect";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  LineChart, Line,
} from "recharts";
import {
  Users, Briefcase, Table2, Euro, TrendingUp, FileSpreadsheet, FileText, X, Percent,
  LayoutGrid, GripVertical, Eye, EyeOff, Check, RotateCcw, CornerDownRight,
} from "lucide-react";
import {
  DndContext, closestCenter, PointerSensor, useSensor, useSensors,
} from "@dnd-kit/core";
import {
  SortableContext, arrayMove, rectSortingStrategy, useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { toast } from "sonner";

const DEFAULT_ORDER = [
  "kpi-totale", "kpi-riconosciuto", "kpi-incidenza-subappalto", "kpi-commesse", "kpi-clienti", "kpi-righe",
  "chart-mensile", "chart-cliente", "panel-commessa", "panel-bef", "panel-subappalto", "panel-da-pagare",
];

const DEFAULT_SPAN = {
  "kpi-totale": 2, "kpi-riconosciuto": 2, "kpi-incidenza-subappalto": 2,
  "kpi-commesse": 2, "kpi-clienti": 2, "kpi-righe": 2,
  "chart-mensile": 4, "chart-cliente": 2,
  "panel-commessa": 6, "panel-bef": 6, "panel-subappalto": 6, "panel-da-pagare": 6,
};

// Altezza di default (px) solo per grafici/pannelli; le card KPI restano ad altezza automatica.
const DEFAULT_HEIGHT = {
  "chart-mensile": 360, "chart-cliente": 360,
  "panel-commessa": 360, "panel-bef": 360, "panel-subappalto": 360, "panel-da-pagare": 360,
};

const GRID_GAP = 20; // gap-5
const MIN_TILE_H = 120;
const MAX_TILE_H = 1200;

const TILE_LABEL = {
  "kpi-totale": "Contabilità Totale", "kpi-riconosciuto": "Riconosciuto (BEF)", "kpi-incidenza-subappalto": "Incidenza Subappalto",
  "kpi-commesse": "Commesse", "kpi-clienti": "Clienti", "kpi-righe": "Righe Registrate",
  "chart-mensile": "Andamento mensile", "chart-cliente": "Per cliente",
  "panel-commessa": "Contabilità per commessa", "panel-bef": "Contabilizzato vs Riconosciuto",
  "panel-subappalto": "Contabilità vs Subappalto", "panel-da-pagare": "Da pagare (modalità Pagato)",
};

const SQUILIBRIO_SOGLIA = 0.10; // ±10%
const statoSquilibrio = (cont, ric) => {
  const base = cont || ric || 1;
  const dev = (ric - cont) / base;
  if (dev <= -SQUILIBRIO_SOGLIA) return { label: "Sotto-fatturato", cls: "bg-rose-50 text-rose-700 border-rose-200", row: "bg-rose-50/50" };
  if (dev >= SQUILIBRIO_SOGLIA) return { label: "Sovra-fatturato", cls: "bg-amber-50 text-amber-700 border-amber-200", row: "bg-amber-50/50" };
  return { label: "Equilibrato", cls: "bg-emerald-50 text-emerald-700 border-emerald-200", row: "" };
};

const StatCard = ({ icon: Icon, label, value, tint, testid }) => (
  <Card data-testid={testid} className="p-5 rounded-md border-slate-200 shadow-sm shadow-slate-200/50 bg-white h-full">
    <div className="flex items-center justify-between">
      <span className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-500">{label}</span>
      <div className={`h-9 w-9 rounded-lg flex items-center justify-center ${tint}`}>
        <Icon className="h-[18px] w-[18px]" strokeWidth={2} />
      </div>
    </div>
    <div className="mt-4 font-heading text-2xl font-bold text-slate-900 tabular-nums tracking-tight">{value}</div>
  </Card>
);

function SortableTile({ id, span, height, editMode, hidden, onToggleHide, label, gridRef, onResize, children }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id, disabled: !editMode });
  const tileRef = useRef(null);
  const setRefs = (el) => { setNodeRef(el); tileRef.current = el; };
  const style = {
    transform: CSS.Transform.toString(transform), transition,
    opacity: isDragging ? 0.4 : 1, zIndex: isDragging ? 40 : "auto",
    gridColumn: `span ${span} / span ${span}`,
    height: height ? `${height}px` : undefined,
  };

  const colStep = () => {
    const gw = gridRef.current ? gridRef.current.offsetWidth : 1200;
    return (gw - 5 * GRID_GAP) / 6 + GRID_GAP;
  };
  const curHeight = () => height || (tileRef.current ? Math.round(tileRef.current.getBoundingClientRect().height) : 200);

  const beginResize = (e, mode) => {
    e.preventDefault(); e.stopPropagation();
    const startX = e.clientX, startY = e.clientY;
    const startSpan = span, startH = curHeight(), step = colStep();
    const onMove = (ev) => {
      const patch = {};
      if (mode !== "h") patch.span = Math.min(6, Math.max(1, Math.round(startSpan + (ev.clientX - startX) / step)));
      if (mode !== "w") patch.height = Math.min(MAX_TILE_H, Math.max(MIN_TILE_H, Math.round(startH + (ev.clientY - startY))));
      onResize(id, patch, false);
    };
    const onUp = () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
      document.body.style.userSelect = "";
      onResize(id, {}, true);
    };
    document.body.style.userSelect = "none";
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  };

  return (
    <div
      ref={setRefs} style={style} data-testid={`tile-${id}`}
      className={`relative ${editMode ? "outline-dashed outline-2 outline-blue-300 outline-offset-4 rounded-md" : ""} ${hidden && editMode ? "opacity-50" : ""}`}
    >
      {editMode && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2 z-30 flex items-center gap-1.5 bg-slate-900 text-white rounded-full px-2.5 py-1 shadow-lg">
          <button {...attributes} {...listeners} data-testid={`tile-drag-${id}`}
            className="cursor-grab active:cursor-grabbing touch-none flex items-center" aria-label={`Sposta ${label}`} title="Trascina per spostare">
            <GripVertical className="h-3.5 w-3.5" />
          </button>
          <span className="w-px h-3 bg-white/30" />
          <button data-testid={`tile-hide-${id}`} onClick={() => onToggleHide(id)}
            className="hover:text-amber-300 flex items-center" aria-label={hidden ? `Mostra ${label}` : `Nascondi ${label}`}
            title={hidden ? "Mostra tile" : "Nascondi tile"}>
            {hidden ? <Eye className="h-3.5 w-3.5" /> : <EyeOff className="h-3.5 w-3.5" />}
          </button>
        </div>
      )}
      {children}
      {editMode && (
        <>
          <div onMouseDown={(e) => beginResize(e, "w")} data-testid={`tile-resize-w-${id}`} title="Trascina per ridimensionare la larghezza"
            className="absolute top-1/2 -right-1 -translate-y-1/2 z-30 w-2 h-12 rounded-full bg-blue-500 hover:bg-blue-600 cursor-col-resize shadow" />
          <div onMouseDown={(e) => beginResize(e, "h")} data-testid={`tile-resize-h-${id}`} title="Trascina per ridimensionare l'altezza"
            className="absolute left-1/2 -bottom-1 -translate-x-1/2 z-30 h-2 w-12 rounded-full bg-blue-500 hover:bg-blue-600 cursor-row-resize shadow" />
          <div onMouseDown={(e) => beginResize(e, "wh")} data-testid={`tile-resize-both-${id}`} title="Trascina per ridimensionare"
            className="absolute -bottom-1.5 -right-1.5 z-30 h-4 w-4 rounded-full bg-blue-600 border-2 border-white cursor-nwse-resize shadow" />
        </>
      )}
    </div>
  );
}

export default function Dashboard() {
  const [summary, setSummary] = useState({ clienti: 0, commesse: 0, righe: 0, totale: 0 });
  const [perCliente, setPerCliente] = useState([]);
  const [perMese, setPerMese] = useState([]);
  const [perCommessa, setPerCommessa] = useState([]);
  const [clienti, setClienti] = useState([]);
  const [commesse, setCommesse] = useState([]);
  const [da, setDa] = useState("");
  const [a, setA] = useState("");
  const [fs, setFs] = useState(null);
  const [nameW, setNameW] = useState(() => {
    const v = parseInt(localStorage.getItem("dash_commessa_name_w") || "208", 10);
    return Number.isFinite(v) ? Math.min(600, Math.max(120, v)) : 208;
  });
  const startNameResize = (e) => {
    e.preventDefault();
    const startX = e.clientX; const startW = nameW;
    const onMove = (ev) => setNameW(Math.min(600, Math.max(120, startW + (ev.clientX - startX))));
    const onUp = () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
      document.body.style.userSelect = "";
      setNameW((w) => { localStorage.setItem("dash_commessa_name_w", String(w)); return w; });
    };
    document.body.style.userSelect = "none";
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  };
  const [cliente, setCliente] = useState([]);
  const [commessa, setCommessa] = useState([]);
  const [benestare, setBenestare] = useState([]);
  const [subappalto, setSubappalto] = useState([]);
  const [daPagare, setDaPagare] = useState([]);

  // layout personalizzabile per utente
  const [order, setOrder] = useState(DEFAULT_ORDER);
  const [hidden, setHidden] = useState([]);
  const [spans, setSpans] = useState(DEFAULT_SPAN);
  const [heights, setHeights] = useState(DEFAULT_HEIGHT);
  const [editMode, setEditMode] = useState(false);
  const gridRef = useRef(null);

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));

  useEffect(() => {
    api.get("/clienti").then((r) => setClienti(r.data));
    api.get("/commesse").then((r) => setCommesse(r.data));
    api.get("/me/preferences/dashboard").then((r) => {
      const saved = r.data || {};
      const savedOrder = Array.isArray(saved.order) ? saved.order.filter((id) => DEFAULT_ORDER.includes(id)) : [];
      const merged = [...savedOrder, ...DEFAULT_ORDER.filter((id) => !savedOrder.includes(id))];
      setOrder(merged);
      setHidden(Array.isArray(saved.hidden) ? saved.hidden.filter((id) => DEFAULT_ORDER.includes(id)) : []);
      const ss = saved.spans && typeof saved.spans === "object" ? saved.spans : {};
      const hh = saved.heights && typeof saved.heights === "object" ? saved.heights : {};
      setSpans({ ...DEFAULT_SPAN, ...Object.fromEntries(Object.entries(ss).filter(([k]) => DEFAULT_ORDER.includes(k))) });
      setHeights({ ...DEFAULT_HEIGHT, ...Object.fromEntries(Object.entries(hh).filter(([k]) => DEFAULT_ORDER.includes(k))) });
    }).catch(() => {});
  }, []);

  const persist = (o, h, s, ht) => {
    api.put("/me/preferences/dashboard", { order: o, hidden: h, spans: s, heights: ht })
      .catch(() => toast.error("Impossibile salvare il layout"));
  };

  const onDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id) return;
    setOrder((prev) => {
      const next = arrayMove(prev, prev.indexOf(active.id), prev.indexOf(over.id));
      persist(next, hidden, spans, heights);
      return next;
    });
  };

  const toggleHide = (id) => {
    setHidden((prev) => {
      const next = prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id];
      persist(order, next, spans, heights);
      return next;
    });
  };

  const onResize = (id, patch, commit) => {
    setSpans((prevS) => {
      const nextS = "span" in patch ? { ...prevS, [id]: patch.span } : prevS;
      setHeights((prevH) => {
        const nextH = "height" in patch ? { ...prevH, [id]: patch.height } : prevH;
        if (commit) persist(order, hidden, nextS, nextH);
        return nextH;
      });
      return nextS;
    });
  };

  const resetLayout = () => {
    setOrder(DEFAULT_ORDER);
    setHidden([]);
    setSpans(DEFAULT_SPAN);
    setHeights(DEFAULT_HEIGHT);
    persist(DEFAULT_ORDER, [], DEFAULT_SPAN, DEFAULT_HEIGHT);
    toast.success("Layout ripristinato");
  };

  const buildParams = useCallback(() => {
    const params = {};
    if (da) params.da = da;
    if (a) params.a = a;
    if (cliente.length) params.cliente_id = cliente.join(",");
    if (commessa.length) params.commessa_id = commessa.join(",");
    return params;
  }, [da, a, cliente, commessa]);

  const load = useCallback(() => {
    const params = buildParams();
    Promise.all([
      api.get("/kpi/summary", { params }),
      api.get("/kpi/per-cliente", { params }),
      api.get("/kpi/per-mese", { params }),
      api.get("/kpi/per-commessa", { params }),
      api.get("/kpi/benestare", { params }),
      api.get("/kpi/subappalto", { params }),
      api.get("/kpi/da-pagare", { params }),
    ]).then(([s, c, m, co, be, sa, dp]) => {
      setSummary(s.data);
      setPerCliente(c.data);
      setBenestare(be.data);
      setSubappalto(sa.data);
      setDaPagare(dp.data);
      setPerMese(m.data.map((x) => ({ ...x, meseLabel: x.mese })));
      setPerCommessa(co.data);
    });
  }, [buildParams]);

  useEffect(() => { load(); }, [load]);

  const hasFilter = Boolean(da || a || cliente.length || commessa.length);
  const clearFilters = () => { setDa(""); setA(""); setCliente([]); setCommessa([]); };
  const commesseOptions = (() => {
    const list = cliente.length ? commesse.filter((c) => cliente.includes(c.cliente_id)) : commesse;
    const byId = Object.fromEntries(list.map((c) => [c.id, c]));
    const byParent = {};
    const roots = [];
    list.forEach((c) => {
      if (c.parent_id && byId[c.parent_id]) (byParent[c.parent_id] = byParent[c.parent_id] || []).push(c);
      else roots.push(c);
    });
    const sortFn = (a, b) => `${a.codice} ${a.nome}`.localeCompare(`${b.codice} ${b.nome}`, "it");
    const out = [];
    roots.sort(sortFn).forEach((r) => {
      out.push({ value: r.id, label: `${r.codice} · ${r.nome}`, depth: 0 });
      (byParent[r.id] || []).sort(sortFn).forEach((k) => out.push({ value: k.id, label: `${k.codice} · ${k.nome}`, depth: 1 }));
    });
    return out;
  })();

  const exportKpi = (kind) =>
    downloadFile(`/kpi/export/${kind}`, `kpi_contabilita.${kind === "excel" ? "xlsx" : "pdf"}`, buildParams())
      .catch(() => toast.error("Errore durante l'esportazione"));

  const maxCommessa = Math.max(...perCommessa.map((x) => x.totale), 1);

  const tiles = {
    "kpi-totale": <StatCard testid="kpi-totale" icon={Euro} label="Contabilità Totale" value={euro(summary.totale)} tint="bg-blue-50 text-blue-700" />,
    "kpi-riconosciuto": <StatCard testid="kpi-riconosciuto" icon={Euro} label="Riconosciuto (BEF)" value={euro(summary.riconosciuto)} tint="bg-emerald-50 text-emerald-600" />,
    "kpi-incidenza-subappalto": <StatCard testid="kpi-incidenza-subappalto" icon={Percent} label="Incidenza Subappalto" value={`${(summary.incidenza_subappalto || 0).toFixed(1)}%`} tint="bg-amber-50 text-amber-600" />,
    "kpi-commesse": <StatCard testid="kpi-commesse" icon={Briefcase} label="Commesse" value={summary.commesse} tint="bg-emerald-50 text-emerald-600" />,
    "kpi-clienti": <StatCard testid="kpi-clienti" icon={Users} label="Clienti" value={summary.clienti} tint="bg-amber-50 text-amber-600" />,
    "kpi-righe": <StatCard testid="kpi-righe" icon={Table2} label="Righe Registrate" value={summary.righe} tint="bg-violet-50 text-violet-600" />,

    "chart-mensile": (
      <Card className="p-6 rounded-md border-slate-200 shadow-sm bg-white h-full flex flex-col">
        <div className="flex items-center gap-2 mb-6">
          <TrendingUp className="h-4 w-4 text-blue-700" />
          <h3 className="font-heading font-semibold text-slate-900">Andamento mensile</h3>
        </div>
        {perMese.length === 0 ? <EmptyChart /> : (
          <div className="flex-1 min-h-0">
          <ResponsiveContainer width="100%" height="100%" minHeight={200}>
            <LineChart data={perMese} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
              <XAxis dataKey="meseLabel" tick={{ fontSize: 12, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 12, fill: "#94a3b8" }} axisLine={false} tickLine={false} width={70} tickFormatter={(v) => `€${(v / 1000).toFixed(0)}k`} />
              <Tooltip formatter={(v) => euro(v)} contentStyle={{ borderRadius: 10, border: "1px solid #e2e8f0", fontSize: 13 }} />
              <Line type="monotone" dataKey="totale" stroke="#1d4ed8" strokeWidth={2.5} dot={{ r: 3, fill: "#1d4ed8" }} activeDot={{ r: 5 }} />
            </LineChart>
          </ResponsiveContainer>
          </div>
        )}
      </Card>
    ),

    "chart-cliente": (
      <Card className="p-6 rounded-md border-slate-200 shadow-sm bg-white h-full flex flex-col">
        <h3 className="font-heading font-semibold text-slate-900 mb-6">Per cliente</h3>
        {perCliente.length === 0 ? <EmptyChart /> : (
          <div className="flex-1 min-h-0">
          <ResponsiveContainer width="100%" height="100%" minHeight={200}>
            <BarChart data={perCliente.slice(0, 6)} layout="vertical" margin={{ left: 8, right: 16 }}>
              <XAxis type="number" hide />
              <YAxis type="category" dataKey="nome" width={90} tick={{ fontSize: 11, fill: "#64748b" }} axisLine={false} tickLine={false} />
              <Tooltip formatter={(v) => euro(v)} contentStyle={{ borderRadius: 10, border: "1px solid #e2e8f0", fontSize: 13 }} />
              <Bar dataKey="totale" fill="#1d4ed8" radius={[0, 6, 6, 0]} barSize={18} />
            </BarChart>
          </ResponsiveContainer>
          </div>
        )}
      </Card>
    ),

    "panel-commessa": (
      <Card className="p-6 rounded-md border-slate-200 shadow-sm bg-white h-full flex flex-col">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-heading font-semibold text-slate-900">Contabilità per commessa</h3>
          <button data-testid="fs-commessa" onClick={() => setFs("commessa")} className="text-slate-400 hover:text-blue-700 p-1 rounded" title="Apri a schermo intero"><Maximize2 className="h-4 w-4" /></button>
        </div>
        <CommessaKpiTable rows={perCommessa} />
      </Card>
    ),

    "panel-bef": (
      <Card className="p-6 rounded-md border-slate-200 shadow-sm bg-white h-full flex flex-col">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-heading font-semibold text-slate-900">Contabilizzato vs Riconosciuto (BEF) per commessa</h3>
          <button data-testid="fs-bef" onClick={() => setFs("bef")} className="text-slate-400 hover:text-blue-700 p-1 rounded" title="Apri a schermo intero"><Maximize2 className="h-4 w-4" /></button>
        </div>
        <BefTable benestare={benestare} />
      </Card>
    ),

    "panel-subappalto": (
      <Card className="p-6 rounded-md border-slate-200 shadow-sm bg-white h-full flex flex-col">
        <h3 className="font-heading font-semibold text-slate-900 mb-4">Contabilità vs Subappalto per commessa</h3>
        {subappalto.length === 0 ? (
          <p className="text-sm text-slate-400 py-8 text-center">Nessun dato di contabilità/subappalto disponibile.</p>
        ) : (
          <div className="overflow-auto flex-1 min-h-0">
            <table className="w-full border-collapse text-sm" data-testid="subappalto-table">
              <thead className="sticky top-0 z-10 bg-slate-50">
                <tr className="bg-slate-50 text-left">
                  <ThD>Commessa</ThD><ThD className="text-right">Contabilità attiva</ThD><ThD className="text-right">Importo Subappalto</ThD>
                  <ThD className="text-right">Margine €</ThD><ThD className="text-right">Margine %</ThD>
                </tr>
              </thead>
              <tbody>
                {subappalto.map((c, i) => (
                  <tr key={c.nome} data-testid={`subappalto-row-${i}`} className="border-b border-slate-100 hover:bg-slate-50">
                    <TdD className="text-slate-700">{c.nome}</TdD>
                    <TdD className="text-right tabular-nums font-semibold text-blue-800">{euro(c.contabilita)}</TdD>
                    <TdD className="text-right tabular-nums font-semibold text-amber-700">{euro(c.subappalto)}</TdD>
                    <TdD className={`text-right tabular-nums font-semibold ${c.margine < 0 ? "text-rose-600" : "text-emerald-700"}`}>{euro(c.margine)}</TdD>
                    <TdD className={`text-right tabular-nums font-semibold ${c.margine < 0 ? "text-rose-600" : "text-slate-700"}`}>{(c.margine_pct || 0).toFixed(1)}%</TdD>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                {(() => {
                  const tc = subappalto.reduce((s, x) => s + x.contabilita, 0);
                  const ts = subappalto.reduce((s, x) => s + x.subappalto, 0);
                  const tm = tc - ts;
                  return (
                    <tr className="bg-slate-50 font-semibold">
                      <TdD className="text-slate-700 uppercase text-xs tracking-widest">Totale</TdD>
                      <TdD className="text-right tabular-nums text-blue-800">{euro(tc)}</TdD>
                      <TdD className="text-right tabular-nums text-amber-700">{euro(ts)}</TdD>
                      <TdD className={`text-right tabular-nums ${tm < 0 ? "text-rose-600" : "text-emerald-700"}`}>{euro(tm)}</TdD>
                      <TdD className={`text-right tabular-nums ${tm < 0 ? "text-rose-600" : "text-slate-700"}`}>{tc ? ((tm / tc) * 100).toFixed(1) : "0.0"}%</TdD>
                    </tr>
                  );
                })()}
              </tfoot>
            </table>
          </div>
        )}
      </Card>
    ),

    "panel-da-pagare": (
      <Card className="p-6 rounded-md border-slate-200 shadow-sm bg-white h-full flex flex-col">
        <h3 className="font-heading font-semibold text-slate-900 mb-1">Da pagare — commesse "Pagato per riga"</h3>
        <p className="text-xs text-slate-400 mb-4">Quanto manca da marcare come pagato rispetto al rendicontato.</p>
        {daPagare.length === 0 ? (
          <p className="text-sm text-slate-400 py-8 text-center">Nessuna commessa in modalità "Pagato per riga" con importi da pagare.</p>
        ) : (
          <div className="overflow-auto flex-1 min-h-0">
            <table className="w-full border-collapse text-sm" data-testid="da-pagare-table">
              <thead className="sticky top-0 z-10 bg-slate-50">
                <tr className="bg-slate-50 text-left">
                  <ThD>Commessa</ThD><ThD className="text-right">Rendicontato</ThD>
                  <ThD className="text-right">Pagato</ThD><ThD className="text-right">Da pagare</ThD>
                </tr>
              </thead>
              <tbody>
                {daPagare.map((c, i) => (
                  <tr key={c.nome} data-testid={`da-pagare-row-${i}`} className="border-b border-slate-100 hover:bg-slate-50">
                    <TdD className="text-slate-700">{c.nome}</TdD>
                    <TdD className="text-right tabular-nums font-semibold text-blue-800">{euro(c.rendicontato)}</TdD>
                    <TdD className="text-right tabular-nums font-semibold text-emerald-700">{euro(c.pagato)}</TdD>
                    <TdD className={`text-right tabular-nums font-semibold ${c.da_pagare > 0 ? "text-rose-600" : "text-slate-700"}`}>{euro(c.da_pagare)}</TdD>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                {(() => {
                  const tr = daPagare.reduce((s, x) => s + x.rendicontato, 0);
                  const tp = daPagare.reduce((s, x) => s + x.pagato, 0);
                  const td = daPagare.reduce((s, x) => s + x.da_pagare, 0);
                  return (
                    <tr className="bg-slate-50 font-semibold">
                      <TdD className="text-slate-700 uppercase text-xs tracking-widest">Totale</TdD>
                      <TdD className="text-right tabular-nums text-blue-800">{euro(tr)}</TdD>
                      <TdD className="text-right tabular-nums text-emerald-700">{euro(tp)}</TdD>
                      <TdD className={`text-right tabular-nums ${td > 0 ? "text-rose-600" : "text-slate-700"}`}>{euro(td)}</TdD>
                    </tr>
                  );
                })()}
              </tfoot>
            </table>
          </div>
        )}
      </Card>
    ),
  };

  const visibleOrder = editMode ? order : order.filter((id) => !hidden.includes(id));

  return (
    <>
      

      <div className="flex-1 overflow-auto px-6 py-6 lg:px-8 space-y-6">
        <PageHeader title="Dashboard" subtitle="Panoramica KPI della contabilità" actions={(
          <div className="flex flex-col items-end gap-3">
            <div className="flex flex-wrap items-center gap-2 order-last">
              {editMode ? (
                <>
                  <Button variant="outline" size="sm" data-testid="dashboard-reset-layout" onClick={resetLayout} className="text-slate-500">
                    <RotateCcw className="h-4 w-4 mr-1.5" /> Ripristina layout
                  </Button>
                  <Button size="sm" data-testid="dashboard-done-edit" onClick={() => setEditMode(false)} className="bg-blue-700 hover:bg-blue-800">
                    <Check className="h-4 w-4 mr-1.5" /> Fine
                  </Button>
                </>
              ) : (
                <>
                  <Button variant="outline" size="sm" data-testid="dashboard-customize" onClick={() => setEditMode(true)}>
                    <LayoutGrid className="h-4 w-4 mr-1.5" /> Personalizza
                  </Button>
                  <Button variant="outline" size="sm" data-testid="kpi-export-excel" onClick={() => exportKpi("excel")}>
                    <FileSpreadsheet className="h-4 w-4 mr-1.5" /> Excel
                  </Button>
                  <Button variant="outline" size="sm" data-testid="kpi-export-pdf" onClick={() => exportKpi("pdf")}>
                    <FileText className="h-4 w-4 mr-1.5" /> PDF
                  </Button>
                </>
              )}
            </div>
            <div className="flex flex-wrap items-end justify-end gap-3">
            <div>
              <Label className="text-[11px] font-semibold uppercase tracking-widest text-slate-500">Da</Label>
              <div className="mt-1"><DatePickerField testid="kpi-da-input" value={da} onChange={setDa} placeholder="Inizio" /></div>
            </div>
            <div>
              <Label className="text-[11px] font-semibold uppercase tracking-widest text-slate-500">A</Label>
              <div className="mt-1"><DatePickerField testid="kpi-a-input" value={a} onChange={setA} placeholder="Fine" /></div>
            </div>
            <div>
              <Label className="text-[11px] font-semibold uppercase tracking-widest text-slate-500">Clienti</Label>
              <div className="mt-1">
                <MultiSelect testid="kpi-cliente-filter" placeholder="Tutti i clienti"
                  value={cliente} onChange={(v) => { setCliente(v); setCommessa([]); }}
                  options={clienti.map((c) => ({ value: c.id, label: c.nome }))} />
              </div>
            </div>
            <div>
              <Label className="text-[11px] font-semibold uppercase tracking-widest text-slate-500">Commesse</Label>
              <div className="mt-1">
                <MultiSelect testid="kpi-commessa-filter" placeholder="Tutte le commesse" searchable
                  value={commessa} onChange={setCommessa}
                  options={commesseOptions} />
              </div>
            </div>
            {hasFilter && (
              <Button variant="ghost" size="sm" data-testid="kpi-clear-filter" onClick={clearFilters} className="h-9 text-slate-500 hover:text-rose-600">
                <X className="h-4 w-4 mr-1" /> Azzera
              </Button>
            )}
          </div>
          </div>
        )}>
      </PageHeader>


        {editMode && (
          <div data-testid="dashboard-edit-banner" className="flex items-center gap-2 rounded-lg bg-blue-50 border border-blue-100 px-4 py-2.5 text-sm text-blue-800">
            <LayoutGrid className="h-4 w-4 flex-shrink-0" />
            Modalità personalizzazione attiva — trascina l'impugnatura in alto per spostare, le maniglie blu sui bordi per ridimensionare (larghezza e altezza), l'occhio per nascondere/mostrare. Le tile nascoste sono in trasparenza.
          </div>
        )}

        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
          <SortableContext items={visibleOrder} strategy={rectSortingStrategy}>
            <div ref={gridRef} className="grid grid-cols-1 lg:grid-cols-6 gap-5 items-start">
              {visibleOrder.map((id) => (
                <SortableTile key={id} id={id} span={spans[id] || DEFAULT_SPAN[id]} height={heights[id]}
                  label={TILE_LABEL[id]} gridRef={gridRef} onResize={onResize}
                  editMode={editMode} hidden={hidden.includes(id)} onToggleHide={toggleHide}>
                  {tiles[id]}
                </SortableTile>
              ))}
            </div>
          </SortableContext>
        </DndContext>
      </div>

      <Dialog open={!!fs} onOpenChange={(o) => !o && setFs(null)}>
        <DialogContent data-testid="kpi-fullscreen-dialog" className="max-w-[95vw] w-[95vw] h-[92vh] p-0 flex flex-col gap-0 [&>button]:hidden">
          <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
            <h2 className="font-heading font-semibold text-lg text-slate-900">
              {fs === "commessa" ? "Contabilità per commessa" : "Contabilizzato vs Riconosciuto (BEF)"}
            </h2>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" data-testid="kpi-fs-export"
                onClick={() => downloadFile(
                  fs === "commessa" ? "/kpi/per-commessa-export" : "/kpi/benestare-export",
                  fs === "commessa" ? "contabilita_per_commessa.xlsx" : "contabilizzato_vs_riconosciuto.xlsx",
                  buildParams()
                ).catch(() => toast.error("Errore durante l'esportazione"))}>
                <FileXls className="h-4 w-4 mr-1.5" /> Esporta XLSX
              </Button>
              <button data-testid="kpi-fs-close" onClick={() => setFs(null)} className="text-slate-400 hover:text-slate-700 p-1 rounded"><XIcon className="h-5 w-5" /></button>
            </div>
          </div>
          <div className="flex flex-wrap items-end gap-3 px-6 py-3 border-b border-slate-100 bg-slate-50">
            <div>
              <Label className="text-[11px] font-semibold uppercase tracking-widest text-slate-500">Da</Label>
              <div className="mt-1"><DatePickerField testid="kpi-fs-da" value={da} onChange={setDa} placeholder="Inizio" /></div>
            </div>
            <div>
              <Label className="text-[11px] font-semibold uppercase tracking-widest text-slate-500">A</Label>
              <div className="mt-1"><DatePickerField testid="kpi-fs-a" value={a} onChange={setA} placeholder="Fine" /></div>
            </div>
            <div className="min-w-[200px]">
              <Label className="text-[11px] font-semibold uppercase tracking-widest text-slate-500">Clienti</Label>
              <div className="mt-1"><MultiSelect testid="kpi-fs-cliente" placeholder="Tutti i clienti"
                value={cliente} onChange={(v) => { setCliente(v); setCommessa([]); }}
                options={clienti.map((c) => ({ value: c.id, label: c.nome }))} /></div>
            </div>
            <div className="min-w-[240px]">
              <Label className="text-[11px] font-semibold uppercase tracking-widest text-slate-500">Commesse</Label>
              <div className="mt-1"><MultiSelect testid="kpi-fs-commessa" placeholder="Tutte le commesse" searchable
                value={commessa} onChange={setCommessa}
                options={commesseOptions} /></div>
            </div>
            {hasFilter && (
              <Button variant="ghost" size="sm" data-testid="kpi-fs-clear" onClick={clearFilters} className="h-9 text-slate-500 hover:text-rose-600">
                <X className="h-4 w-4 mr-1" /> Azzera
              </Button>
            )}
          </div>
          <div className="flex-1 min-h-0 overflow-auto p-6">
            {fs === "commessa" ? <CommessaKpiTable rows={perCommessa} fullscreen /> : <BefTable benestare={benestare} fullscreen />}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

const ThD = ({ children, className = "" }) => (
  <th className={`px-3 py-2.5 text-xs font-semibold uppercase tracking-widest text-slate-500 border-b border-slate-200 ${className}`}>{children}</th>
);
const TdD = ({ children, className = "", ...rest }) => (
  <td className={`px-3 py-2.5 ${className}`} {...rest}>{children}</td>
);

const EmptyChart = () => (
  <div className="h-[280px] flex items-center justify-center text-sm text-slate-400">
    Nessun dato disponibile
  </div>
);
